/**
 * Lightweight i18n for the end-user verification flow.
 *
 * Deliberately dependency-free: the flow has well under a hundred strings, and
 * pulling in a full i18n framework would add build weight for no benefit.
 *
 * English source text doubles as the lookup key. A missing translation therefore
 * degrades to the original English wording rather than to a bare key — a partially
 * translated screen still reads correctly, which matters when new strings land
 * upstream before anyone has translated them.
 *
 * The chosen language is persisted so a page reload — or the mobile handoff, which
 * opens a fresh tab — keeps the language the user picked.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { de } from './de.js';

export type Lang = 'de' | 'en';

const DICTIONARIES: Record<Lang, Record<string, string>> = {
  de,
  en: {}, // English is the source language — no lookup needed
};

const STORAGE_KEY = 'idswyft_lang';

/** Read the persisted language, falling back to German. */
function initialLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    // localStorage unavailable (private mode, embedded webview) — fall through
  }
  return 'de';
}

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (text: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persisting is best-effort; the session still works without it
    }
  }, []);

  const t = useCallback(
    (text: string) => DICTIONARIES[lang][text] ?? text,
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Access the translator. Usable outside a provider — it then returns the English
 * source text unchanged, so components remain safe to render in isolation (tests,
 * storybook, the dev portal).
 */
export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  return { lang: 'en', setLang: () => {}, t: (text: string) => text };
}

const LANG_NAMES: Record<Lang, string> = { de: 'Deutsch', en: 'English' };

/**
 * Language toggle for the verification header.
 *
 * Sized for touch rather than for tidiness: each option is a 44px-tall target,
 * matching the minimum recommended by the platform accessibility guidelines. The
 * flow is used one-handed on phones, and a switch nobody can hit is a switch that
 * leaves the user stuck on a language they may not read.
 */
export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { lang, setLang } = useI18n();
  const options: Lang[] = ['de', 'en'];

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-lg p-1 ${className}`}
      style={{
        border: '1px solid var(--rule, rgba(0,0,0,0.14))',
        background: 'var(--panel, transparent)',
      }}
      role="group"
      aria-label={lang === 'de' ? 'Sprache wählen' : 'Choose language'}
    >
      {options.map(option => {
        const active = lang === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => setLang(option)}
            aria-pressed={active}
            aria-label={LANG_NAMES[option]}
            title={LANG_NAMES[option]}
            className="rounded-md font-semibold uppercase transition-colors"
            style={{
              minWidth: 52,
              minHeight: 44,
              padding: '0 14px',
              fontSize: 15,
              letterSpacing: '0.06em',
              cursor: 'pointer',
              border: 'none',
              background: active ? 'var(--ink)' : 'transparent',
              color: active ? 'var(--paper)' : 'var(--mid)',
            }}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}
